/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type AxiosInstance } from 'axios';
import {
  type GCPProductVehicleImageResponse,
  type GCPProductVehicleImageOptions,
} from '../interface';
import { getAxios } from '@/lib/axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { createQuery } from '@/utils/createReactQuery';
import { type QueryClient } from '@tanstack/react-query';
import environmentConfig from '@/config/environment';

const URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-product-data/v1/product-vehicle-image`;

const getGcpProductVehicleImage = async (
  options: GCPProductVehicleImageOptions,
  axiosInstance?: AxiosInstance,
): Promise<GCPProductVehicleImageResponse> => {
  const response = await getAxios(axiosInstance).get<GCPProductVehicleImageResponse>(URL, {
    params: options,
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });
  return response.data;
};

const { useData: useProductVehicleImageInfo, prefetch: prefetchProductVehicleImageInfo } =
  createQuery<GCPProductVehicleImageResponse, GCPProductVehicleImageOptions>(
    'product-vehicle-images',
    async (options, axiosInstance) => getGcpProductVehicleImage({ ...options }, axiosInstance),
  );

export const useGetGcpProductVehicleImage = (props: {
  partCode: string;
  country: string;
  vehicleId: string;
  enabled: boolean;
}) => {
  const { partCode, country, vehicleId, enabled } = props;
  const options = {
    partCode,
    country,
    vehicleId,
    enabled,
  };

  return useProductVehicleImageInfo(options);
};

export const prefetchProductVehicleImage = async (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  options: GCPProductVehicleImageOptions,
) => {
  return prefetchProductVehicleImageInfo(queryClient, options, axiosInstance);
};
